import { useQuery, useMutation } from "@tanstack/react-query";
import { trackEvent } from "@/hooks/useTracking";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GeogridScanTheater } from "@/components/geogrid-scan-theater";
import {
  OutOfCreditsDialog,
  parseOutOfCreditsError,
  type OutOfCreditsDetails,
} from "@/components/out-of-credits-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Grid3X3, Plus, Play, Clock, X, Search, Target, Map as MapIcon, BarChart3,
  PieChart, Users, Loader2, CheckCircle2, Info, Download, RefreshCw, Trash2,
  ChevronLeft, ChevronRight, ExternalLink, ArrowUpDown, TrendingUp, TrendingDown,
  ArrowRight, Share2,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import type { Location, Keyword, GeogridScan, GridPoint } from "@shared/schema";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  LineChart, Line, ResponsiveContainer, ScatterChart, Scatter,
  XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell,
  PieChart as RPieChart, Pie,
} from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { GoogleMap, useJsApiLoader, OverlayViewF, OverlayView } from "@react-google-maps/api";

const CHART_COLORS = [
  "#22c55e", "#3b82f6", "#eab308", "#f97316", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f43f5e", "#6366f1", "#84cc16",
];


function computeWeightedScore(rank: number): number {
  if (rank === 1) return 100;
  if (rank === 2) return 50;
  if (rank === 3) return 30;
  if (rank >= 4 && rank <= 10) return 10;
  return 0;
}

function computeCoverage(gridData: number[][]): number {
  const flat = gridData.flat().filter(r => r > 0);
  if (flat.length === 0) return 0;
  const top3 = flat.filter(r => r <= 3).length;
  return Math.round((top3 / flat.length) * 100);
}

function getRankColor(rank: number): string {
  if (rank <= 0 || rank > 20) return "#dc2626";
  if (rank === 1) return "#166534";
  if (rank <= 3) return "#22c55e";
  if (rank <= 10) return "#eab308";
  return "#f97316";
}


interface ScanWithPoints extends GeogridScan {
  gridPoints?: GridPoint[];
}

interface CompetitorData {
  name: string;
  address: string;
  score: number;
  recordCount: number;
  top3Count: number;
  avgRank: number;
  atgr: number;
  solv: number;
  avgRating: number;
  totalReviews: number;
  isTarget: boolean;
  categories: string[];
  placeId: string;
}

function buildCompetitorData(points: GridPoint[], targetName?: string): CompetitorData[] {
  const totalPoints = points.length;
  const map = new Map<string, {
    score: number; count: number; top3: number; rankSum: number;
    rankedCount: number; address: string; ratingSum: number; ratingCount: number;
    reviewMax: number; categories: Set<string>; placeId: string;
  }>();

  for (const pt of points) {
    const listings = (pt.gmbListings as any[]) || [];
    for (const listing of listings) {
      const name = listing.title || listing.business || "";
      if (!name) continue;
      const existing = map.get(name) || {
        score: 0, count: 0, top3: 0, rankSum: 0, rankedCount: 0,
        address: "", ratingSum: 0, ratingCount: 0, reviewMax: 0,
        categories: new Set<string>(), placeId: "",
      };
      const rank = listing.position || listing.rank || 21;
      existing.score += computeWeightedScore(rank);
      existing.count += 1;
      if (rank <= 3) existing.top3 += 1;
      existing.rankSum += rank;
      existing.rankedCount += 1;
      if (!existing.address && listing.address) existing.address = listing.address;
      if (listing.type) existing.categories.add(listing.type);
      if (listing.placeId && !existing.placeId) existing.placeId = listing.placeId;
      const rating = parseFloat(listing.rating || "0");
      if (rating > 0) { existing.ratingSum += rating; existing.ratingCount += 1; }
      const reviews = parseInt(listing.reviews || "0");
      if (reviews > existing.reviewMax) existing.reviewMax = reviews;
      map.set(name, existing);
    }
  }

  const normalizedTarget = (targetName || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return Array.from(map.entries())
    .map(([name, data]) => ({
      name,
      address: data.address,
      score: data.score,
      recordCount: data.count,
      top3Count: data.top3,
      avgRank: data.rankedCount > 0 ? parseFloat((data.rankSum / data.rankedCount).toFixed(2)) : 21,
      atgr: data.rankedCount > 0 ? parseFloat((data.rankSum / data.rankedCount).toFixed(2)) : 21,
      solv: totalPoints > 0 ? parseFloat(((data.top3 / totalPoints) * 100).toFixed(1)) : 0.0,
      avgRating: data.ratingCount > 0 ? parseFloat((data.ratingSum / data.ratingCount).toFixed(1)) : 0,
      totalReviews: data.reviewMax,
      isTarget: normalizedTarget.length > 3 && name.toLowerCase().replace(/[^a-z0-9]/g, "").includes(normalizedTarget),
      categories: Array.from(data.categories).slice(0, 3),
      placeId: data.placeId,
    }))
    .sort((a, b) => b.score - a.score);
}

function StatsBar({ scan }: { scan: GeogridScan }) {
  const gridData = (scan.gridData as number[][]) || [];
  const flat = gridData.flat();
  const found = flat.filter(r => r > 0 && r <= 20).length;
  const total = flat.length;
  const solv = scan.solvScore ? parseFloat(scan.solvScore) : computeCoverage(gridData);
  const avgRank = scan.avgRank || 0;

  return (
    <div className="grid grid-cols-3 gap-3">
      <Card className="bg-amber-500/10 border-amber-500/30">
        <CardContent className="p-4 text-center">
          <p className="text-xs font-medium text-amber-400 uppercase">Avg Rank</p>
          <p className="text-2xl font-bold font-mono text-amber-400" data-testid="text-avg-rank">
            {avgRank > 0 ? avgRank.toFixed(1) : "--"}
          </p>
        </CardContent>
      </Card>
      <Card className={`${solv >= 50 ? "bg-emerald-500/10 border-emerald-500/30" : "bg-red-500/10 border-red-500/30"}`}>
        <CardContent className="p-4 text-center">
          <p className={`text-xs font-medium uppercase ${solv >= 50 ? "text-emerald-400" : "text-red-400"}`}>SoLV</p>
          <p className={`text-2xl font-bold font-mono ${solv >= 50 ? "text-emerald-400" : "text-red-400"}`} data-testid="text-solv">
            {solv.toFixed(1)}%
          </p>
        </CardContent>
      </Card>
      <Card className="bg-emerald-500/10 border-emerald-500/30">
        <CardContent className="p-4 text-center">
          <p className="text-xs font-medium text-emerald-400 uppercase">Grid Points</p>
          <p className="text-2xl font-bold font-mono text-emerald-400" data-testid="text-grid-points">
            {found}/{total}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}


function getRankLabel(rank: number): string {
  if (rank > 0 && rank <= 20) return String(rank);
  return "20+";
}

function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="inline-flex text-yellow-500 text-xs">
      {"★".repeat(full)}{half ? "½" : ""}{"☆".repeat(empty)}
    </span>
  );
}

function GeoGridMapViewInner({ scan, points, centerLat, centerLng, apiKey, keyword }: {
  scan: GeogridScan;
  points: GridPoint[];
  centerLat: number;
  centerLng: number;
  apiKey: string;
  keyword?: string;
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    id: "google-maps-script",
  });

  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [mapsAuthFailed, setMapsAuthFailed] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);

  // When a Google Maps key fails authentication (HTTP-referrer restriction that
  // doesn't include this domain, disabled/deleted key, billing off, or the Maps
  // JS API not enabled) the loader promise never resolves OR rejects — so
  // `isLoaded` stays false and `loadError` stays undefined, leaving an infinite
  // spinner. Google surfaces this case only via the global gm_authFailure
  // callback, so we hook it (and add a hard timeout safety net) to degrade to the
  // CSS grid fallback instead of spinning forever.
  useEffect(() => {
    const prev = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => setMapsAuthFailed(true);
    return () => {
      (window as any).gm_authFailure = prev;
    };
  }, []);

  useEffect(() => {
    if (isLoaded || loadError) return;
    const t = setTimeout(() => setLoadTimedOut(true), 10000);
    return () => clearTimeout(t);
  }, [isLoaded, loadError]);

  const markers = useMemo(() => {
    return points.map((pt, i) => ({
      lat: parseFloat(pt.lat),
      lng: parseFloat(pt.lng),
      rank: pt.rank != null ? pt.rank : 21,
      listings: (pt.gmbListings as any[]) || [],
      gridPosition: pt.gridPosition || "",
      index: i,
    })).filter(m => !isNaN(m.lat) && !isNaN(m.lng));
  }, [points]);

  const selectedMarker = selectedIdx !== null ? markers.find(m => m.index === selectedIdx) : null;

  const onMapLoad = (map: google.maps.Map) => {
    if (markers.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    markers.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
    map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
  };

  // If the Google Maps JS API fails to load (e.g. the API key is invalid or has
  // an HTTP-referrer restriction that doesn't include this domain), fall back to
  // the CSS grid so the scan results are still visible instead of an endless spinner.
  if (loadError || mapsAuthFailed || (loadTimedOut && !isLoaded)) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500" data-testid="text-map-load-error">
          <Info className="w-3.5 h-3.5 shrink-0" />
          Interactive map unavailable (Google Maps key error) — showing grid view instead.
        </div>
        <CssGridFallback scan={scan} points={points} keyword={keyword} />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (markers.length === 0) {
    return <p className="text-muted-foreground text-sm py-8 text-center">No grid point coordinates available for map view</p>;
  }

  return (
    <div className="space-y-4">
      <div className="relative rounded-lg overflow-hidden border" style={{ height: 600 }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={{ lat: centerLat, lng: centerLng }}
          zoom={11}
          onLoad={onMapLoad}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          }}
          onClick={() => setSelectedIdx(null)}
        >
          {markers.map((m) => (
            <OverlayViewF
              key={m.index}
              position={{ lat: m.lat, lng: m.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                className="cursor-pointer"
                style={{ transform: "translate(-50%, -50%)" }}
                onClick={(e) => { e.stopPropagation(); setSelectedIdx(selectedIdx === m.index ? null : m.index); }}
                data-testid={`map-marker-${m.index}`}
              >
                <div
                  className="rounded-full flex items-center justify-center text-white font-bold shadow-lg border-2 border-white/40"
                  style={{
                    backgroundColor: getRankColor(m.rank),
                    width: 36,
                    height: 36,
                    fontSize: m.rank > 0 && m.rank <= 20 ? (m.rank >= 10 ? 11 : 13) : 10,
                  }}
                >
                  {getRankLabel(m.rank)}
                </div>
              </div>
            </OverlayViewF>
          ))}
        </GoogleMap>

        {selectedMarker && (
          <div
            className="absolute top-0 right-0 w-full sm:w-[360px] max-w-full h-full bg-white dark:bg-card border-l shadow-2xl z-50 flex flex-col"
            data-testid="grid-point-detail-panel"
          >
            <div className="p-4 border-b bg-gray-50 dark:bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm text-foreground">
                  Results for "{keyword || "keyword"}"
                </h3>
                <button
                  onClick={() => setSelectedIdx(null)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="close-detail-panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Target className="w-3.5 h-3.5 text-red-500" />
                {selectedMarker.lat.toFixed(7)}, {selectedMarker.lng.toFixed(7)}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {selectedMarker.listings.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-8">No listings found at this grid point</p>
              ) : (
                selectedMarker.listings.map((listing: any, j: number) => {
                  const pos = listing.position || j + 1;
                  const isTarget = pos === selectedMarker.rank;
                  return (
                    <div
                      key={j}
                      className={`flex gap-3 p-3 rounded-lg border ${isTarget ? "bg-primary/5 border-primary/30" : "bg-background border-border"}`}
                      data-testid={`listing-item-${j}`}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: getRankColor(pos) }}
                      >
                        {pos}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isTarget ? "text-primary" : "text-foreground"}`}>
                          {listing.title || listing.business || listing.address || "Business"}
                        </p>
                        {listing.address && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {listing.address}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {listing.rating !== undefined && listing.rating !== "0.000" && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              {parseFloat(listing.rating || "0").toFixed(1)}
                              <StarRating rating={parseFloat(listing.rating || "0")} />
                              {listing.reviews && <span>({listing.reviews})</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <ScanSettingsBadges scan={scan} keyword={keyword} />
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {[
            { color: "#166534", label: "#1" },
            { color: "#22c55e", label: "#2-3" },
            { color: "#eab308", label: "#4-10" },
            { color: "#f97316", label: "#11-20" },
            { color: "#dc2626", label: "20+" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="text-xs text-muted-foreground">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScanSettingsBadges({ scan, keyword }: { scan: GeogridScan; keyword?: string }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="scan-settings-badges">
      {keyword && (
        <span
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted text-xs text-foreground"
          data-testid="badge-scan-keyword"
        >
          <span className="text-muted-foreground">Keyword:</span>
          <span className="font-medium">{keyword}</span>
        </span>
      )}
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted text-xs text-foreground"
        data-testid="badge-scan-radius"
      >
        <span className="text-muted-foreground">Radius:</span>
        <span className="font-medium">{scan.radius} mi</span>
      </span>
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted text-xs text-foreground"
        data-testid="badge-scan-grid-size"
      >
        <span className="text-muted-foreground">Grid:</span>
        <span className="font-medium">{scan.gridSize}×{scan.gridSize}</span>
      </span>
    </div>
  );
}

function CssGridFallback({ scan, points, keyword }: {
  scan: GeogridScan;
  points: GridPoint[];
  keyword?: string;
}) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const gridSize = scan.gridSize || 7;
  const gridData = scan.gridData as number[][] | null;

  const selectedPoint = selectedIdx !== null ? points[selectedIdx] : null;
  const selectedListings = selectedPoint ? ((selectedPoint.gmbListings as any[]) || []) : [];

  return (
    <div className="space-y-4">
      <div className="relative rounded-lg overflow-hidden border bg-gradient-to-br from-gray-900 to-gray-800" style={{ minHeight: 500 }}>
        <div
          className="grid gap-1.5 p-4 mx-auto"
          style={{
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            maxWidth: Math.min(gridSize * 56, 500),
          }}
        >
          {(gridData || []).flat().map((rank, idx) => {
            return (
              <div
                key={idx}
                className="aspect-square rounded-md flex items-center justify-center text-white font-bold shadow-md cursor-pointer hover:scale-110 transition-transform border border-white/10"
                style={{
                  backgroundColor: getRankColor(rank),
                  fontSize: rank >= 10 ? 11 : 13,
                }}
                onClick={() => setSelectedIdx(selectedIdx === idx ? null : idx)}
                data-testid={`grid-cell-${idx}`}
              >
                {getRankLabel(rank)}
              </div>
            );
          })}
        </div>

        {selectedIdx !== null && selectedPoint && (
          <div
            className="absolute top-0 right-0 w-full sm:w-[340px] max-w-full h-full bg-white dark:bg-card border-l shadow-2xl z-50 flex flex-col"
            data-testid="grid-point-detail-panel"
          >
            <div className="p-4 border-b bg-gray-50 dark:bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm text-foreground">
                  Results for "{keyword || "keyword"}"
                </h3>
                <button
                  onClick={() => setSelectedIdx(null)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="close-detail-panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs text-muted-foreground">
                Grid position: Row {Math.floor(selectedIdx / gridSize) + 1}, Col {(selectedIdx % gridSize) + 1}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {selectedListings.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-8">No listings found at this grid point</p>
              ) : (
                selectedListings.map((listing: any, j: number) => {
                  const pos = listing.position || j + 1;
                  return (
                    <div
                      key={j}
                      className="flex gap-3 p-3 rounded-lg border bg-background border-border"
                      data-testid={`listing-item-${j}`}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: getRankColor(pos) }}
                      >
                        {pos}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate text-foreground">
                          {listing.title || listing.business || listing.address || "Business"}
                        </p>
                        {listing.address && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {listing.address}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {listing.rating !== undefined && listing.rating !== "0.000" && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              {parseFloat(listing.rating || "0").toFixed(1)}
                              <StarRating rating={parseFloat(listing.rating || "0")} />
                              {listing.reviews && <span>({listing.reviews})</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <ScanSettingsBadges scan={scan} keyword={keyword} />
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {[
            { color: "#166534", label: "#1" },
            { color: "#22c55e", label: "#2-3" },
            { color: "#eab308", label: "#4-10" },
            { color: "#f97316", label: "#11-20" },
            { color: "#dc2626", label: "20+" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="text-xs text-muted-foreground">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GeoGridMapView({ scan, points, centerLat, centerLng, keyword }: {
  scan: GeogridScan;
  points: GridPoint[];
  centerLat: number;
  centerLng: number;
  keyword?: string;
}) {
  const { data: mapsConfig, isLoading: mapsConfigLoading } = useQuery<{ apiKey: string | null }>({
    queryKey: ["/api/config/maps-key"],
  });

  if (mapsConfigLoading) {
    return (
      <div className="flex items-center justify-center h-[500px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!mapsConfig?.apiKey) {
    return <CssGridFallback scan={scan} points={points} keyword={keyword} />;
  }

  return <GeoGridMapViewInner scan={scan} points={points} centerLat={centerLat} centerLng={centerLng} apiKey={mapsConfig.apiKey} keyword={keyword} />;
}

function ProximityTab({ competitors, locationName }: { competitors: CompetitorData[]; locationName: string }) {
  const top20 = competitors.slice(0, 20);
  if (top20.length === 0) return <p className="text-muted-foreground text-sm py-8 text-center">No competitor data available</p>;

  const avgReach = top20.reduce((s, c) => s + c.recordCount, 0) / top20.length;
  const avgPower = top20.reduce((s, c) => s + c.score, 0) / top20.length;
  const maxScore = Math.max(...top20.map(c => c.score), 1);

  const data = top20.map(c => ({
    ...c,
    x: c.recordCount,
    y: c.score,
    z: Math.max((c.score / maxScore) * 800, 40),
    quadrant: c.recordCount >= avgReach
      ? (c.score >= avgPower ? "Dominant Leader" : "Page 2 Purgatory")
      : (c.score >= avgPower ? "Sniper" : "Invisible"),
  }));

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <h3 className="text-sm font-semibold">Opportunity Matrix</h3>
        <div className="group relative">
          <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
          <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-card border rounded-md p-2 text-xs z-10 w-56">
            Dot size = Power Score. X = how many grid points a business appears in. Y = weighted ranking score.
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 30, bottom: 50, left: 50 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis
              type="number" dataKey="x" name="Reach"
              label={{ value: "Reach (Appearances)", position: "insideBottom", offset: -15, style: { fontSize: 12, fill: "#6B7280" } }}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              type="number" dataKey="y" name="Power"
              label={{ value: "Power (Score)", angle: -90, position: "insideLeft", offset: -10, style: { fontSize: 12, fill: "#6B7280" } }}
              tick={{ fontSize: 11 }}
            />
            <ZAxis type="number" dataKey="z" range={[40, 800]} />
            <Tooltip content={({ payload }) => {
              if (!payload?.length) return null;
              const d = payload[0].payload;
              return (
                <div className="bg-card border rounded-lg p-3 text-xs shadow-lg">
                  <p className="font-semibold mb-1">{d.name}</p>
                  <p>Power Score: <span className="font-mono font-bold">{d.score}</span></p>
                  <p>Total Reach: <span className="font-mono">{d.recordCount}</span></p>
                  <p>Map Pack Wins: <span className="font-mono">{d.top3Count}</span></p>
                  <p>Avg Rank: <span className="font-mono">{d.avgRank}</span></p>
                  <p>SoLV: <span className="font-mono">{d.solv}%</span></p>
                  <p className="text-muted-foreground mt-1 italic">{d.quadrant}</p>
                </div>
              );
            }} />
            <Scatter data={data} fill="#64748b">
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.isTarget ? "#F6642A" : "#0081DF"} opacity={entry.isTarget ? 1 : 0.5} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CompetitorCard({ c, rank }: { c: CompetitorData; rank: number }) {
  const mapsUrl = c.placeId
    ? `https://www.google.com/maps/place/?q=place_id:${c.placeId}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.name)}`;

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3.5 border-b last:border-0 ${c.isTarget ? "bg-primary/5 border-primary/20" : "hover:bg-muted/30"}`}
      data-testid={`competitor-row-${rank}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 mt-0.5 ${
        c.isTarget ? "bg-primary/15 text-primary" : "bg-muted/50 text-muted-foreground"
      }`}>
        {c.name.charAt(0).toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm font-semibold truncate hover:underline ${c.isTarget ? "text-primary" : "text-foreground"}`}
            data-testid={`link-competitor-${rank}`}
          >
            {c.name}
          </a>
          <ExternalLink className="w-3 h-3 text-muted-foreground/50 shrink-0" />
        </div>
        {c.address && (
          <p className="text-[11px] text-muted-foreground truncate mt-0.5">{c.address}</p>
        )}
        {c.categories.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {c.categories.map((cat, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground">{cat}</span>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-3 items-start text-right shrink-0">
        <div>
          <p className="text-[10px] text-muted-foreground">AGR</p>
          <p className="text-sm font-bold font-mono">{c.avgRank}</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground">ATGR</p>
          <p className="text-sm font-bold font-mono">{c.atgr}</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground">SoLV</p>
          <p className="text-sm font-bold font-mono">{c.solv}%</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground">Rating</p>
          <p className="text-sm font-bold font-mono">
            {c.avgRating > 0 ? (
              <span className="flex items-center gap-0.5">
                {c.avgRating} <span className="text-yellow-500 text-[10px]">★</span>
                {c.totalReviews > 0 && <span className="text-muted-foreground text-[10px] font-normal">({c.totalReviews})</span>}
              </span>
            ) : "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

function CompetitorsTab({ competitors, locationName }: { competitors: CompetitorData[]; locationName: string }) {
  const [sortField, setSortField] = useState<"score" | "avgRank" | "atgr" | "solv" | "avgRating">("score");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const target = competitors.find(c => c.isTarget);
  const sorted = useMemo(() => {
    const copy = competitors.filter(c => !c.isTarget);
    copy.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (sortField === "avgRank" || sortField === "atgr") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDir === "desc" ? bVal - aVal : aVal - bVal;
    });
    return copy.slice(0, 25);
  }, [competitors, sortField, sortDir]);

  if (competitors.length === 0) return <p className="text-muted-foreground text-sm py-8 text-center">No competitor data available</p>;

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir(field === "avgRank" || field === "atgr" ? "asc" : "desc");
    }
  };

  const downloadCsv = () => {
    const headers = ["Rank", "Business", "Address", "AGR", "ATGR", "SoLV %", "Rating", "Reviews", "Score"];
    const rows = sorted.map((c, i) => [
      i + 1,
      `"${c.name.replace(/"/g, '""')}"`,
      `"${(c.address || "").replace(/"/g, '""')}"`,
      c.avgRank,
      c.atgr,
      c.solv,
      c.avgRating,
      c.totalReviews,
      c.score,
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `competitors-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1.5 flex-wrap">
          {(["score", "avgRank", "atgr", "solv", "avgRating"] as const).map(field => {
            const labels: Record<string, string> = { score: "Score", avgRank: "AGR", atgr: "ATGR", solv: "SoLV", avgRating: "Rating" };
            return (
              <button
                key={field}
                onClick={() => handleSort(field)}
                className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                  sortField === field
                    ? "bg-primary/10 border-primary/30 text-primary font-semibold"
                    : "bg-transparent border-border text-muted-foreground hover:bg-muted/30"
                }`}
                data-testid={`sort-${field}`}
              >
                {labels[field]} {sortField === field ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </button>
            );
          })}
        </div>
        <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="btn-download-csv">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {target && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center text-sm font-bold text-primary shrink-0">
              {target.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-primary truncate">{target.name}</p>
              <p className="text-[11px] text-muted-foreground truncate">{target.address || locationName}</p>
            </div>
            <div className="flex gap-3 text-right shrink-0">
              <div><p className="text-[10px] text-muted-foreground">AGR</p><p className="text-sm font-bold font-mono">{target.avgRank}</p></div>
              <div><p className="text-[10px] text-muted-foreground">ATGR</p><p className="text-sm font-bold font-mono">{target.atgr}</p></div>
              <div><p className="text-[10px] text-muted-foreground">SoLV</p><p className="text-sm font-bold font-mono">{target.solv}%</p></div>
              <div><p className="text-[10px] text-muted-foreground">Rating</p><p className="text-sm font-bold font-mono">{target.avgRating} <span className="text-yellow-500 text-[10px]">★</span> {target.totalReviews > 0 && <span className="text-muted-foreground text-[10px] font-normal">({target.totalReviews})</span>}</p></div>
              <div><p className="text-[10px] text-muted-foreground">Score</p><p className="text-sm font-bold font-mono">{target.score}</p></div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden" data-testid="competitors-table">
        {sorted.map((c, i) => (
          <CompetitorCard key={c.name} c={c} rank={i + 1} />
        ))}
      </div>
    </div>
  );
}

function MarketShareTab({ competitors }: { competitors: CompetitorData[] }) {
  const top8 = competitors.filter(c => c.top3Count > 0).slice(0, 8);
  const totalTop3 = top8.reduce((s, c) => s + c.top3Count, 0);

  if (top8.length === 0) return <p className="text-muted-foreground text-sm py-8 text-center">No top 3 data available</p>;

  const data = top8.map((c, i) => ({
    name: c.name,
    value: c.top3Count,
    pct: totalTop3 > 0 ? ((c.top3Count / totalTop3) * 100).toFixed(1) : "0",
    fill: c.isTarget ? "#16a34a" : CHART_COLORS[i % CHART_COLORS.length],
    isTarget: c.isTarget,
  }));

  return (
    <div className="flex flex-col h-full">
      <h3 className="text-sm font-semibold mb-3 flex-shrink-0">Share of Top 3 Positions</h3>
      <div className="flex flex-1 min-h-0 items-stretch gap-6">
        <div className="flex-1 min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <RPieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius="80%"
                label={({ pct }) => `${pct}%`}
                labelLine={false}
              >
                {data.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.fill}
                    stroke={entry.isTarget ? "#16a34a" : "transparent"}
                    strokeWidth={entry.isTarget ? 3 : 0}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value: number, name: string) => [`${value} wins`, name]} />
            </RPieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-3 min-w-[200px] flex-shrink-0 self-center">
          {data.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.fill }} />
              <span className={`text-xs truncate max-w-[160px] ${d.isTarget ? "font-bold text-emerald-500" : ""}`}>{d.name}</span>
              <span className="text-xs text-muted-foreground ml-auto font-mono">{d.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CategoriesTab({ points, scanId }: { points: GridPoint[]; scanId?: string }) {
  const [enriching, setEnriching] = useState(false);
  const [enriched, setEnriched] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const { toast } = useToast();

  const handleVerify = async () => {
    if (!scanId || verifying) return;
    setVerifying(true);
    try {
      const res = await apiRequest("POST", `/api/geogrid-scans/scan/${scanId}/verify-categories`, {});
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/geogrid-scans/scan", scanId] });
      if (data.changes?.length > 0) {
        toast({ title: "Categories updated", description: `${data.changes.length} categories changed` });
      } else {
        toast({ title: "Categories verified", description: "All categories are up to date" });
      }
    } catch {
      toast({ title: "Verification failed", variant: "destructive" });
    } finally {
      setVerifying(false);
    }
  };

  const hasCategories = useMemo(() => {
    for (const pt of points) {
      const listings = (pt.gmbListings as any[]) || [];
      for (const listing of listings) {
        if ((listing.position || listing.rank || 21) > 3) continue;
        if (listing.type && listing.type !== "") return true;
      }
    }
    return false;
  }, [points]);

  useEffect(() => {
    if (!hasCategories && scanId && !enriching && !enriched) {
      setEnriching(true);
      apiRequest("POST", `/api/geogrid-scans/scan/${scanId}/enrich-categories`, {})
        .then(r => r.json())
        .then((data) => {
          if (data.enriched > 0) {
            queryClient.invalidateQueries({ queryKey: ["/api/geogrid-scans/scan", scanId] });
            toast({ title: "Categories loaded", description: `Found categories for ${data.enriched} listings` });
          }
          setEnriched(true);
        })
        .catch(() => setEnriched(true))
        .finally(() => setEnriching(false));
    }
  }, [hasCategories, scanId, enriching, enriched]);

  const categoryCount = useMemo(() => {
    const map = new Map<string, number>();
    for (const pt of points) {
      const listings = (pt.gmbListings as any[]) || [];
      for (const listing of listings) {
        if ((listing.position || listing.rank || 21) > 3) continue;
        const type = listing.type || listing.category || "";
        if (type) {
          map.set(type, (map.get(type) || 0) + 1);
        }
      }
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [points]);

  const totalPoints = points.length;
  const totalTop3Slots = totalPoints * 3;

  if (enriching) return (
    <div className="flex flex-col items-center justify-center h-full text-center py-12">
      <Loader2 className="w-10 h-10 text-brand-orange animate-spin mb-3" />
      <p className="text-sm font-medium">Loading business categories...</p>
      <p className="text-muted-foreground/60 text-xs mt-1">Looking up Google Business Profile categories</p>
    </div>
  );

  if (categoryCount.length === 0) return (
    <div className="flex flex-col items-center justify-center h-full text-center py-12">
      <BarChart3 className="w-10 h-10 text-muted-foreground/40 mb-3" />
      <p className="text-muted-foreground text-sm">No category data available</p>
      <p className="text-muted-foreground/60 text-xs mt-1">Category lookup is not currently available</p>
      {scanId && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleVerify}
          disabled={verifying}
          data-testid="button-verify-categories-empty"
          className="text-xs gap-1.5 mt-4"
        >
          {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {verifying ? "Checking..." : "Re-check Categories"}
        </Button>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div>
          <h3 className="text-sm font-semibold">Business categories in Top 3 results for this keyword</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{totalTop3Slots} total top-3 slots across {totalPoints} grid points</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleVerify}
          disabled={verifying}
          data-testid="button-verify-categories"
          className="text-xs gap-1.5"
        >
          {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {verifying ? "Verifying..." : "Verify"}
        </Button>
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={categoryCount} margin={{ top: 10, right: 20, bottom: 80, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, angle: -35, textAnchor: "end" }}
              interval={0}
              tickLine={false}
            />
            <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip
              cursor={{ fill: "rgba(255,255,255,0.05)" }}
              content={({ payload }) => {
                if (!payload?.length) return null;
                const d = payload[0].payload;
                const pct = totalTop3Slots > 0 ? ((d.count / totalTop3Slots) * 100).toFixed(1) : "0";
                return (
                  <div className="bg-card border rounded-lg p-3 text-xs shadow-lg">
                    <p className="font-semibold">{d.name}</p>
                    <p className="text-muted-foreground">{d.count} appearances ({pct}% of top 3 slots)</p>
                  </div>
                );
              }}
            />
            <Bar dataKey="count" name="Appearances" radius={[6, 6, 0, 0]} maxBarSize={80}>
              {categoryCount.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MiniGrid({ gridData, size = 60 }: { gridData: number[][]; size?: number }) {
  if (!gridData || gridData.length === 0) {
    return (
      <div style={{ width: size, height: size }} className="rounded bg-muted/50 flex items-center justify-center">
        <Grid3X3 className="w-4 h-4 text-muted-foreground/40" />
      </div>
    );
  }
  const rows = gridData.length;
  const cols = gridData[0]?.length || 0;
  const cellSize = size / Math.max(rows, cols);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`rotate(-90, ${size / 2}, ${size / 2})`}>
        {gridData.map((row, ri) =>
          row.map((rank, ci) => (
            <rect
              key={`${ri}-${ci}`}
              x={ci * cellSize}
              y={ri * cellSize}
              width={cellSize}
              height={cellSize}
              fill={rank <= 0 ? "#374151" : getRankColor(rank)}
              stroke="#0A0A0F"
              strokeWidth={0.5}
              rx={0.5}
            />
          ))
        )}
      </g>
    </svg>
  );
}

function MiniGridLoading({ size = 36, gridDim = 7 }: { size?: number; gridDim?: number }) {
  const cellSize = size / gridDim;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2;

  return (
    <div style={{ width: size, height: size }} className="relative">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rounded">
        {Array.from({ length: gridDim }).map((_, ri) =>
          Array.from({ length: gridDim }).map((_, ci) => (
            <rect
              key={`${ri}-${ci}`}
              x={ci * cellSize}
              y={ri * cellSize}
              width={cellSize}
              height={cellSize}
              fill="#1E1E2E"
              stroke="#0A0A0F"
              strokeWidth={0.5}
              rx={0.5}
            />
          ))
        )}
        <defs>
          <clipPath id={`radar-clip-${size}`}>
            <rect x="0" y="0" width={size} height={size} />
          </clipPath>
        </defs>
        <g clipPath={`url(#radar-clip-${size})`}>
          <g className="animate-[radar-sweep_2s_linear_infinite]" style={{ transformOrigin: `${cx}px ${cy}px` }}>
            <path
              d={`M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r * Math.sin(Math.PI / 3)} ${cy - r * Math.cos(Math.PI / 3)} Z`}
              fill="url(#radar-gradient)"
              opacity={0.6}
            />
          </g>
          <defs>
            <linearGradient id="radar-gradient" x1="0" y1="0" x2="1" y2="0" gradientTransform="rotate(30)">
              <stop offset="0%" stopColor="#F6642A" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#F6642A" stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle cx={cx} cy={cy} r={r * 0.15} fill="none" stroke="#F6642A" strokeWidth={0.5} opacity={0.3} />
          <circle cx={cx} cy={cy} r={r * 0.5} fill="none" stroke="#F6642A" strokeWidth={0.5} opacity={0.2} />
          <circle cx={cx} cy={cy} r={r * 0.85} fill="none" stroke="#F6642A" strokeWidth={0.5} opacity={0.1} />
        </g>
      </svg>
      <style>{`
        @keyframes radar-sweep {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function ScanTimeline({ scans, activeScanId, onSelect }: {
  scans: GeogridScan[];
  activeScanId: string | null;
  onSelect: (scanId: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollBy = useCallback((dir: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 200, behavior: "smooth" });
    }
  }, []);

  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [activeScanId]);

  if (scans.length === 0) return null;

  const sorted = [...scans].sort((a, b) => {
    const da = a.scannedAt ? new Date(a.scannedAt).getTime() : 0;
    const db = b.scannedAt ? new Date(b.scannedAt).getTime() : 0;
    return db - da;
  });

  return (
    <div className="relative mt-4" data-testid="scan-timeline">
      <Button
        size="icon"
        variant="ghost"
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-muted"
        onClick={() => scrollBy(-1)}
        data-testid="timeline-scroll-left"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide px-10 py-2"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {sorted.map((s) => {
          const gridData = (s.gridData as number[][]) || [];
          const isActive = s.id === activeScanId;
          const date = s.scannedAt ? new Date(s.scannedAt) : null;
          return (
            <button
              key={s.id}
              ref={isActive ? activeRef : undefined}
              onClick={() => onSelect(s.id)}
              className={`flex-shrink-0 flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-all cursor-pointer ${
                isActive
                  ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                  : "border-border/50 bg-card hover:border-border hover:bg-muted/50"
              }`}
              data-testid={`timeline-scan-${s.id}`}
            >
              <MiniGrid gridData={gridData} size={56} />
              {date && (
                <div className="text-center">
                  <p className="text-[10px] font-medium leading-tight">
                    {date.toLocaleDateString("en-US", { month: "short", day: "numeric" })},
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight">
                    {date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                  </p>
                </div>
              )}
            </button>
          );
        })}
      </div>
      <Button
        size="icon"
        variant="ghost"
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-muted"
        onClick={() => scrollBy(1)}
        data-testid="timeline-scroll-right"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

function ScanResultsView({ scan, points, locationName, centerLat, centerLng, keyword, onRunScan, isScanning, scanLabel, onDeleteScan, onExport, keywordScans, activeScanId, onSelectScan }: {
  scan: GeogridScan;
  points: GridPoint[];
  locationName: string;
  centerLat: number;
  centerLng: number;
  keyword?: string;
  onRunScan?: () => void;
  isScanning?: boolean;
  scanLabel?: string;
  onDeleteScan?: () => void;
  onExport?: () => void;
  keywordScans?: GeogridScan[];
  activeScanId?: string | null;
  onSelectScan?: (scanId: string) => void;
}) {
  const competitors = useMemo(() => buildCompetitorData(points, locationName), [points, locationName]);

  return (
    <div className="space-y-4">
      <Tabs defaultValue="geogrid" className="w-full">
        <TabsList className="w-full flex overflow-x-auto">
          <TabsTrigger value="geogrid" className="flex-1 gap-1.5" data-testid="tab-geogrid">
            <Grid3X3 className="w-3.5 h-3.5" /> GeoGrid
          </TabsTrigger>
          <TabsTrigger value="proximity" className="flex-1 gap-1.5" data-testid="tab-proximity">
            <Target className="w-3.5 h-3.5" /> Proximity
          </TabsTrigger>
          <TabsTrigger value="competitors" className="flex-1 gap-1.5" data-testid="tab-competitors">
            <Users className="w-3.5 h-3.5" /> Competitors
          </TabsTrigger>
          <TabsTrigger value="market" className="flex-1 gap-1.5" data-testid="tab-market">
            <PieChart className="w-3.5 h-3.5" /> Market Share
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex-1 gap-1.5" data-testid="tab-categories">
            <BarChart3 className="w-3.5 h-3.5" /> Categories
          </TabsTrigger>
        </TabsList>

        <TabsContent value="geogrid" className="mt-4 space-y-4">
          {onRunScan && (
            <div className="flex items-start gap-2 flex-wrap">
              <div className="flex flex-col gap-1">
                <Button
                  size="sm"
                  onClick={onRunScan}
                  disabled={isScanning}
                  data-testid="btn-run-scan-floating"
                  className="gap-1.5"
                >
                  {isScanning ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning...</>
                  ) : (
                    <>Repeat <ChevronRight className="w-3.5 h-3.5" /></>
                  )}
                </Button>
                {keyword && (
                  <span className="text-xs text-muted-foreground px-1 truncate max-w-[200px]" data-testid="text-repeat-keyword">
                    {keyword}
                  </span>
                )}
              </div>
              {onDeleteScan && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDeleteScan}
                  data-testid="btn-delete-scan-toolbar"
                  className="gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </Button>
              )}
              {onExport && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onExport}
                  data-testid="btn-export-toolbar"
                  className="gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </Button>
              )}
            </div>
          )}
          <GeoGridMapView scan={scan} points={points} centerLat={centerLat} centerLng={centerLng} keyword={keyword} />
          <StatsBar scan={scan} />
          {keywordScans && keywordScans.length > 1 && onSelectScan && (
            <ScanTimeline scans={keywordScans} activeScanId={activeScanId || scan.id} onSelect={onSelectScan} />
          )}
        </TabsContent>
        <TabsContent value="proximity" className="mt-4">
          <div className="h-[650px] overflow-y-auto rounded-lg border bg-card p-4">
            <ProximityTab competitors={competitors} locationName={locationName} />
          </div>
        </TabsContent>
        <TabsContent value="competitors" className="mt-4">
          <div className="h-[650px] overflow-y-auto rounded-lg border bg-card p-4">
            <CompetitorsTab competitors={competitors} locationName={locationName} />
          </div>
        </TabsContent>
        <TabsContent value="market" className="mt-4">
          <div className="h-[650px] overflow-y-auto rounded-lg border bg-card p-4">
            <MarketShareTab competitors={competitors} />
          </div>
        </TabsContent>
        <TabsContent value="categories" className="mt-4">
          <div className="h-[650px] overflow-y-auto rounded-lg border bg-card p-4">
            <CategoriesTab points={points} scanId={scan.id} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProcessingView({ scan }: { scan: GeogridScan }) {
  const progress = scan.progress || 0;
  return (
    <div className="flex flex-col items-center py-16 text-center">
      <Loader2 className="w-12 h-12 text-brand-orange animate-spin mb-4" />
      <h3 className="text-lg font-semibold">Analyzing Your Market</h3>
      <p className="text-sm text-muted-foreground mt-2">
        Running scan... {progress}% complete
      </p>
      <div className="w-64 h-2 bg-muted rounded-full mt-4 overflow-hidden">
        <div className="h-full bg-brand-orange rounded-full transition-all" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

function KeywordList({
  keywords, selectedKeywordId, onSelect, locationId,
}: {
  keywords: Keyword[];
  selectedKeywordId: string | null;
  onSelect: (id: string) => void;
  locationId: string;
}) {
  const { toast } = useToast();
  const [newKeyword, setNewKeyword] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const addKeywordMutation = useMutation({
    mutationFn: async (keyword: string) => {
      await apiRequest("POST", "/api/keywords", { locationId, keyword });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/keywords", locationId] });
      setNewKeyword("");
      setDialogOpen(false);
      toast({ title: "Keyword added" });
    },
  });

  const deleteKeywordMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/keywords/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/keywords", locationId] });
      toast({ title: "Keyword removed" });
    },
  });

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-1">
        <CardTitle className="text-sm font-semibold">Tracked Keywords</CardTitle>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="button-add-keyword">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Keyword</DialogTitle>
            </DialogHeader>
            <div className="flex gap-2 mt-4">
              <Input
                placeholder="e.g., best pizza near me"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && newKeyword.trim()) addKeywordMutation.mutate(newKeyword); }}
                data-testid="input-new-keyword"
              />
              <Button
                onClick={() => addKeywordMutation.mutate(newKeyword)}
                disabled={!newKeyword.trim() || addKeywordMutation.isPending}
                data-testid="button-save-keyword"
              >
                Add
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {keywords.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center">
            <Search className="w-8 h-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No keywords tracked yet</p>
            <p className="text-xs text-muted-foreground mt-1">Add keywords to start tracking</p>
          </div>
        ) : (
          keywords.map((kw) => (
            <div
              key={kw.id}
              className={`flex items-center justify-between p-2.5 rounded-md cursor-pointer hover:bg-accent/50 transition-colors ${
                selectedKeywordId === kw.id ? "bg-primary/10 border border-primary/20" : ""
              }`}
              onClick={() => onSelect(kw.id)}
              data-testid={`button-keyword-${kw.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{kw.keyword}</p>
                {kw.avgRank && (
                  <span className="text-xs text-muted-foreground">Avg: #{kw.avgRank.toFixed(1)}</span>
                )}
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={(e) => { e.stopPropagation(); deleteKeywordMutation.mutate(kw.id); }}
                data-testid={`button-delete-keyword-${kw.id}`}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function CompareGrid({ gridData, size = 180 }: { gridData: number[][]; size?: number }) {
  if (!gridData || gridData.length === 0) {
    return (
      <div style={{ width: size, height: size }} className="rounded bg-muted/30 flex items-center justify-center">
        <Grid3X3 className="w-8 h-8 text-muted-foreground/30" />
      </div>
    );
  }
  const rows = gridData.length;
  const cols = gridData[0]?.length || 0;
  const cellSize = size / Math.max(rows, cols);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`rotate(-90, ${size / 2}, ${size / 2})`}>
        {gridData.map((row, ri) =>
          row.map((rank, ci) => {
            const showLabel = rank > 0 && rank <= 20;
            const fontSize = cellSize > 14 ? 10 : cellSize > 8 ? 7 : 0;
            return (
              <g key={`${ri}-${ci}`}>
                <rect
                  x={ci * cellSize}
                  y={ri * cellSize}
                  width={cellSize}
                  height={cellSize}
                  fill={rank <= 0 ? "#374151" : getRankColor(rank)}
                  stroke="#0A0A0F"
                  strokeWidth={0.5}
                  rx={1}
                />
                {showLabel && fontSize > 0 && (
                  <text
                    x={ci * cellSize + cellSize / 2}
                    y={ri * cellSize + cellSize / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="white"
                    fontSize={fontSize}
                    fontWeight="bold"
                    transform={`rotate(90, ${ci * cellSize + cellSize / 2}, ${ri * cellSize + cellSize / 2})`}
                  >
                    {rank}
                  </text>
                )}
              </g>
            );
          })
        )}
      </g>
    </svg>
  );
}

function pctChange(oldVal: number, newVal: number): number | null {
  if (oldVal === 0) return null;
  return ((newVal - oldVal) / Math.abs(oldVal)) * 100;
}

function CompareModal({ scans, keywords, locationName, open, onOpenChange }: {
  scans: GeogridScan[];
  keywords: Keyword[];
  locationName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const completedScans = useMemo(() => scans.filter(s => s.status === "completed"), [scans]);

  const keywordComparisons = useMemo(() => {
    if (!open) return [];
    const result: {
      keyword: string;
      latest: GeogridScan;
      worst: GeogridScan;
      latestArp: number;
      worstArp: number;
      arpDiff: number;
      arpPctChange: number | null;
      latestAtgr: number;
      worstAtgr: number;
      atgrPctChange: number | null;
      latestSolv: number;
      worstSolv: number;
      solvPctChange: number | null;
    }[] = [];

    for (const kw of keywords) {
      const kwScans = completedScans
        .filter(s => s.keywordId === kw.id && s.avgRank != null && s.avgRank > 0)
        .sort((a, b) => {
          const da = a.scannedAt ? new Date(a.scannedAt).getTime() : 0;
          const db = b.scannedAt ? new Date(b.scannedAt).getTime() : 0;
          return db - da;
        });

      if (kwScans.length < 2) continue;

      const latest = kwScans[0];

      const worstCandidates = kwScans.filter(s => s.id !== latest.id);
      if (worstCandidates.length === 0) continue;

      const worst = [...worstCandidates].sort((a, b) => {
        return (b.avgRank || 0) - (a.avgRank || 0);
      })[0];

      const latestArp = latest.avgRank || 0;
      const worstArp = worst.avgRank || 0;
      const latestGridData = (latest.gridData as number[][]) || [];
      const worstGridData = (worst.gridData as number[][]) || [];

      const latestFlat = latestGridData.flat().filter(r => r > 0 && r <= 20);
      const worstFlat = worstGridData.flat().filter(r => r > 0 && r <= 20);
      const latestAtgr = latestFlat.length > 0 ? latestFlat.reduce((s, r) => s + r, 0) / latestFlat.length : 21;
      const worstAtgr = worstFlat.length > 0 ? worstFlat.reduce((s, r) => s + r, 0) / worstFlat.length : 21;
      const latestSolv = latest.solvScore ? parseFloat(latest.solvScore) : computeCoverage(latestGridData);
      const worstSolv = worst.solvScore ? parseFloat(worst.solvScore) : computeCoverage(worstGridData);

      const arpDiff = latestArp - worstArp;

      result.push({
        keyword: kw.keyword,
        latest,
        worst,
        latestArp,
        worstArp,
        arpDiff,
        arpPctChange: pctChange(worstArp, latestArp),
        latestAtgr: parseFloat(latestAtgr.toFixed(2)),
        worstAtgr: parseFloat(worstAtgr.toFixed(2)),
        atgrPctChange: pctChange(worstAtgr, latestAtgr),
        latestSolv,
        worstSolv,
        solvPctChange: pctChange(worstSolv, latestSolv),
      });
    }
    return result;
  }, [completedScans, keywords, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="compare-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ArrowUpDown className="w-5 h-5" />
            Latest vs Worst – {locationName}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Compare the most recent scan against the worst performing scan in history by Average Rank Position (ARP).
          </p>
        </DialogHeader>

        {keywordComparisons.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center">
            <Grid3X3 className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">Need at least 2 scans per keyword to compare.</p>
          </div>
        ) : (
          <div className="space-y-8 mt-2">
            {keywordComparisons.map((comp) => (
              <div key={comp.keyword} data-testid={`compare-keyword-${comp.keyword}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold">{comp.keyword}</h3>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch gap-3">
                  <div className="flex-1 rounded-xl border bg-card p-4" data-testid={`compare-latest-${comp.keyword}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Latest</Badge>
                    </div>
                    <div className="flex justify-center mb-3">
                      <CompareGrid gridData={(comp.latest.gridData as number[][]) || []} size={180} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{comp.keyword}</p>
                      <div className="flex items-center gap-3 text-xs flex-wrap">
                        <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 font-mono">
                          ARP: {comp.latestArp.toFixed(2)}
                        </Badge>
                        <span className="text-muted-foreground">ATGR: {comp.latestAtgr}</span>
                        <span className="text-muted-foreground">SoLV: {comp.latestSolv.toFixed(2)}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {comp.latest.scannedAt ? new Date(comp.latest.scannedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                      </p>
                    </div>
                  </div>

                  <div className="flex sm:flex-col flex-row items-center justify-center gap-2 px-2 py-3 sm:py-0 min-w-[100px]">
                    <ArrowRight className="w-5 h-5 text-muted-foreground hidden sm:block" />
                    <div className={`text-sm font-bold whitespace-nowrap ${comp.arpDiff < 0 ? "text-emerald-400" : comp.arpDiff > 0 ? "text-red-400" : "text-muted-foreground"}`}>
                      {comp.arpDiff < 0 ? "" : "+"}{comp.arpDiff.toFixed(1)} ARP
                    </div>
                    {comp.arpPctChange !== null ? (
                      <div className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        comp.arpPctChange < 0 ? "bg-emerald-500/20 text-emerald-400" : comp.arpPctChange > 0 ? "bg-red-500/20 text-red-400" : "bg-muted text-muted-foreground"
                      }`} data-testid={`compare-arp-pct-${comp.keyword}`}>
                        ARP {comp.arpPctChange > 0 ? "+" : ""}{comp.arpPctChange.toFixed(1)}%
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted whitespace-nowrap">ARP N/A</div>
                    )}
                    {comp.atgrPctChange !== null ? (
                      <div className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        comp.atgrPctChange < 0 ? "bg-emerald-500/20 text-emerald-400" : comp.atgrPctChange > 0 ? "bg-red-500/20 text-red-400" : "bg-muted text-muted-foreground"
                      }`} data-testid={`compare-atgr-pct-${comp.keyword}`}>
                        ATGR {comp.atgrPctChange > 0 ? "+" : ""}{comp.atgrPctChange.toFixed(1)}%
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted whitespace-nowrap">ATGR N/A</div>
                    )}
                    {comp.solvPctChange !== null ? (
                      <div className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        comp.solvPctChange > 0 ? "bg-emerald-500/20 text-emerald-400" : comp.solvPctChange < 0 ? "bg-red-500/20 text-red-400" : "bg-muted text-muted-foreground"
                      }`} data-testid={`compare-solv-pct-${comp.keyword}`}>
                        SoLV {comp.solvPctChange > 0 ? "+" : ""}{comp.solvPctChange.toFixed(1)}%
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted whitespace-nowrap">SoLV N/A</div>
                    )}
                  </div>

                  <div className="flex-1 rounded-xl border bg-card p-4" data-testid={`compare-worst-${comp.keyword}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingDown className="w-4 h-4 text-red-400" />
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">Worst</Badge>
                    </div>
                    <div className="flex justify-center mb-3">
                      <CompareGrid gridData={(comp.worst.gridData as number[][]) || []} size={180} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{comp.keyword}</p>
                      <div className="flex items-center gap-3 text-xs flex-wrap">
                        <Badge variant="outline" className="text-red-400 border-red-500/30 font-mono">
                          ARP: {comp.worstArp.toFixed(2)}
                        </Badge>
                        <span className="text-muted-foreground">ATGR: {comp.worstAtgr}</span>
                        <span className="text-muted-foreground">SoLV: {comp.worstSolv.toFixed(2)}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {comp.worst.scannedAt ? new Date(comp.worst.scannedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function GeoGridPage() {
  const { selectedLocationId } = useLocationStore();
  const { toast } = useToast();
  const [selectedKeywordId, setSelectedKeywordId] = useState<string | null>(null);
  const [customRadius, setCustomRadius] = useState("6.5");
  const [customGridSize, setCustomGridSize] = useState(7);
  const [activeScanId, setActiveScanId] = useState<string | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [outOfCreditsOpen, setOutOfCreditsOpen] = useState(false);
  const [outOfCreditsDetails, setOutOfCreditsDetails] = useState<OutOfCreditsDetails | null>(null);

  const { data: locations } = useQuery<Location[]>({ queryKey: ["/api/locations"] });
  // Validate selectedLocationId against the actual locations list — if the
  // stored ID is stale (e.g. the location was deleted), fall back to the
  // first available location instead of querying with a dead ID.
  const validSelected = locations?.find((l) => l.id === selectedLocationId);
  const locationId = validSelected?.id || locations?.[0]?.id;
  const currentLocation = validSelected || locations?.[0];

  const { data: keywords = [], isLoading: kwLoading } = useQuery<Keyword[]>({
    queryKey: ["/api/keywords", locationId],
    enabled: !!locationId,
  });

  const { data: scans = [] } = useQuery<GeogridScan[]>({
    queryKey: ["/api/geogrid-scans", locationId],
    enabled: !!locationId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasActive = data.some(s => s.status === "pending" || s.status === "processing");
      return hasActive ? 3000 : false;
    },
  });

  useEffect(() => {
    setActiveScanId(null);
    setSelectedKeywordId(null);
  }, [locationId]);

  useEffect(() => {
    if (!selectedKeywordId && keywords.length > 0) {
      setSelectedKeywordId(keywords[0].id);
    }
  }, [keywords, selectedKeywordId]);

  useEffect(() => {
    if (scans.length > 0 && selectedKeywordId) {
      const completedScans = scans.filter(s => s.status === "completed" && s.keywordId === selectedKeywordId);
      const mostRecent = completedScans[0];
      if (mostRecent) {
        setActiveScanId(mostRecent.id);
      } else {
        setActiveScanId(null);
      }
    }
  }, [selectedKeywordId]);

  useEffect(() => {
    if (!activeScanId && scans.length > 0 && selectedKeywordId) {
      const match = scans.find(s => s.status === "completed" && s.keywordId === selectedKeywordId);
      if (match) setActiveScanId(match.id);
    }
  }, [scans]);

  const { data: scanDetail, error: scanDetailError } = useQuery<ScanWithPoints>({
    queryKey: ["/api/geogrid-scans/scan", activeScanId],
    queryFn: async () => {
      const res = await fetch(`/api/geogrid-scans/scan/${activeScanId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch scan");
      return res.json();
    },
    enabled: !!activeScanId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return (data.status === "pending" || data.status === "processing") ? 2000 : false;
    },
  });

  const isProcessing = scanDetail && (scanDetail.status === "pending" || scanDetail.status === "processing");
  const isFailed = scanDetail?.status === "failed";

  // Track scan IDs that started in this browser session so we know to play the
  // theater reveal vs. just rendering the historical results view directly.
  const [theaterScanIds, setTheaterScanIds] = useState<Set<string>>(new Set());
  const [theaterDoneIds, setTheaterDoneIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (scanDetail && (scanDetail.status === "pending" || scanDetail.status === "processing")) {
      setTheaterScanIds(prev => prev.has(scanDetail.id) ? prev : new Set(prev).add(scanDetail.id));
    }
  }, [scanDetail?.id, scanDetail?.status]);

  useEffect(() => {
    if (scanDetail && (scanDetail.status === "completed" || scanDetail.status === "failed")) {
      queryClient.invalidateQueries({ queryKey: ["/api/geogrid-scans", locationId] });
    }
  }, [scanDetail?.status]);

  const selectedScan = useMemo(() => {
    if (activeScanId && scanDetail) return scanDetail;
    const kw = selectedKeywordId || keywords[0]?.id;
    if (!kw) return undefined;
    return scans.find(s => s.status === "completed" && s.keywordId === kw);
  }, [activeScanId, scanDetail, scans, selectedKeywordId, keywords]);

  const locationNeedsGeocode = currentLocation && (
    !currentLocation.gbpPlaceId ||
    !currentLocation.lat || !currentLocation.lng ||
    (currentLocation.lat === 0 && currentLocation.lng === 0)
  );

  const geocodeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/locations/${locationId}/geocode`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      toast({ title: "Location detected", description: "Coordinates and Place ID have been set. You can now run scans!" });
    },
    onError: (err: any) => {
      toast({ title: "Detection failed", description: err.message, variant: "destructive" });
    },
  });

  const runScanMutation = useMutation({
    mutationFn: async (overrides?: { gridSize?: number; radius?: string; keywordId?: string }) => {
      const res = await apiRequest("POST", "/api/geogrid-scans", {
        locationId,
        keywordId: overrides?.keywordId || selectedKeywordId || keywords[0]?.id,
        gridSize: overrides?.gridSize || customGridSize,
        radius: overrides?.radius || customRadius,
        placeId: currentLocation?.gbpPlaceId || "",
      });
      return res.json();
    },
    onSuccess: (data) => {
      trackEvent('gbp_geogrid_viewed', { scan_id: data.id });
      setActiveScanId(data.id);
      queryClient.invalidateQueries({ queryKey: ["/api/geogrid-scans", locationId] });
      queryClient.refetchQueries({ queryKey: ["/api/billing/geogrid-credits"] });
      toast({ title: "Scan started", description: "Your GeoGrid scan is running..." });
    },
    onError: (err: any) => {
      const details = parseOutOfCreditsError(err);
      if (details) {
        setOutOfCreditsDetails(details);
        setOutOfCreditsOpen(true);
        trackEvent('geogrid_scan_blocked_no_credits', {
          balance: details.balance,
          cost: details.cost,
          grid_size: details.gridSize,
          tier: details.tier,
        });
        return;
      }
      toast({ title: "Scan failed", description: err.message, variant: "destructive" });
    },
  });

  const deleteScanMutation = useMutation({
    mutationFn: async (scanId: string) => {
      await apiRequest("DELETE", `/api/geogrid-scans/scan/${scanId}`);
    },
    onSuccess: (_, deletedScanId) => {
      trackEvent('geogrid_scan_deleted', { scan_id: deletedScanId });
      if (activeScanId === deletedScanId) setActiveScanId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/geogrid-scans", locationId] });
      toast({ title: "Scan deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Delete failed", description: err.message, variant: "destructive" });
    },
  });

  const retryScanMutation = useMutation({
    mutationFn: async (scanId: string) => {
      const res = await apiRequest("POST", `/api/geogrid-scans/scan/${scanId}/retry`, {});
      return res.json();
    },
    onSuccess: (_, scanId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/geogrid-scans", locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/geogrid-scans/scan", scanId] });
      setTheaterScanIds(prev => prev.has(scanId) ? prev : new Set(prev).add(scanId));
      toast({ title: "Retrying scan", description: "Re-running with the same keyword and grid settings." });
    },
    onError: (err: any) => {
      toast({ title: "Retry failed", description: err.message, variant: "destructive" });
    },
  });

  const displayScan = scanDetail || selectedScan;
  const displayPoints = (displayScan as ScanWithPoints)?.gridPoints || [];

  if (kwLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-[400px]" />
          <Skeleton className="h-[400px] lg:col-span-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-geogrid-title">
            GeoGrid Rank Tracking
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time local search rankings across your service area
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCompareOpen(true)}
          disabled={scans.filter(s => s.status === "completed").length < 2}
          className="gap-1.5"
          data-testid="button-compare"
        >
          <ArrowUpDown className="w-4 h-4" /> Latest vs Worst
        </Button>
      </div>

      <CompareModal
        scans={scans}
        keywords={keywords}
        locationName={currentLocation?.name || ""}
        open={compareOpen}
        onOpenChange={setCompareOpen}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-4">
          <KeywordList
            keywords={keywords}
            selectedKeywordId={selectedKeywordId}
            onSelect={setSelectedKeywordId}
            locationId={locationId || ""}
          />

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Scan Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Radius (miles)</label>
                  <Input
                    type="number"
                    min={0.5}
                    max={50}
                    step={0.5}
                    value={customRadius}
                    onChange={(e) => setCustomRadius(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="input-radius"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Grid Size</label>
                  <Select value={String(customGridSize)} onValueChange={(val) => setCustomGridSize(parseInt(val))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="select-grid">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[3,5,7,9,11,13,15,17,19,21].map(v => (
                        <SelectItem key={v} value={String(v)}>{v}x{v} ({v*v} pts)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {locationNeedsGeocode && (
                <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs space-y-2">
                  <p className="text-amber-400 font-medium">Location needs setup</p>
                  <p className="text-muted-foreground">This location is missing coordinates or a Google Place ID. Click below to auto-detect from the address.</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/20"
                    onClick={() => geocodeMutation.mutate()}
                    disabled={geocodeMutation.isPending}
                    data-testid="button-geocode"
                  >
                    {geocodeMutation.isPending ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Detecting...</>
                    ) : (
                      <><MapIcon className="w-3.5 h-3.5 mr-1.5" /> Auto-Detect Location</>
                    )}
                  </Button>
                </div>
              )}

              {(() => {
                const activeKw = keywords.find(k => k.id === selectedKeywordId) || keywords[0];
                if (!activeKw) return null;
                return (
                  <div className="mt-2 px-3 py-2 rounded-md bg-muted/60 border border-border" data-testid="text-active-keyword">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Scanning keyword</p>
                    <p className="text-sm font-semibold text-foreground truncate">{activeKw.keyword}</p>
                  </div>
                );
              })()}

              <Button
                className="w-full"
                onClick={() => runScanMutation.mutate()}
                disabled={runScanMutation.isPending || isProcessing as boolean || !keywords.length || locationNeedsGeocode}
                data-testid="button-run-scan"
              >
                {runScanMutation.isPending || isProcessing ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Scanning...</>
                ) : (
                  <><Play className="w-4 h-4 mr-1.5" /> Run Scan ({customGridSize * customGridSize} pts)</>
                )}
              </Button>
            </CardContent>
          </Card>

          {scans.filter(s => s.status === "completed" || s.status === "pending" || s.status === "processing").length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Scan History</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-64 overflow-y-auto">
                {(() => {
                  const visible = scans.filter(s => s.status === "completed" || s.status === "pending" || s.status === "processing");
                  const byKeyword = new Map<string, GeogridScan[]>();
                  for (const s of visible) {
                    const kwId = s.keywordId || "unknown";
                    if (!byKeyword.has(kwId)) byKeyword.set(kwId, []);
                    byKeyword.get(kwId)!.push(s);
                  }
                  return Array.from(byKeyword.entries()).map(([kwId, kwScans]) => {
                    const kw = keywords.find(k => k.id === kwId);
                    const sorted = [...kwScans].sort((a, b) => {
                      const statusOrder = (s: GeogridScan) => (s.status === "pending" || s.status === "processing") ? 0 : 1;
                      if (statusOrder(a) !== statusOrder(b)) return statusOrder(a) - statusOrder(b);
                      const da = a.scannedAt ? new Date(a.scannedAt).getTime() : 0;
                      const db = b.scannedAt ? new Date(b.scannedAt).getTime() : 0;
                      return db - da;
                    });
                    return (
                      <div key={kwId} data-testid={`scan-history-keyword-${kwId}`}>
                        <p className="text-xs font-medium truncate mb-1.5">{kw?.keyword || "Unknown"}</p>
                        <div className="flex gap-1.5 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
                          {sorted.map((s) => {
                            const isPendingOrProcessing = s.status === "pending" || s.status === "processing";
                            const gridData = (s.gridData as number[][]) || [];
                            const isActive = activeScanId === s.id;
                            return (
                              <button
                                key={s.id}
                                onClick={() => {
                                  setActiveScanId(s.id);
                                  if (s.keywordId) setSelectedKeywordId(s.keywordId);
                                }}
                                className={`flex-shrink-0 p-1 rounded border transition-all ${
                                  isActive
                                    ? "border-brand-orange bg-brand-orange/10 ring-1 ring-brand-orange/30"
                                    : isPendingOrProcessing
                                      ? "border-brand-orange/30 bg-brand-orange/5"
                                      : "border-border/30 hover:border-border hover:bg-muted/50"
                                }`}
                                title={isPendingOrProcessing ? "Scanning..." : (s.scannedAt ? new Date(s.scannedAt).toLocaleString() : "")}
                                data-testid={`button-scan-${s.id}`}
                              >
                                {isPendingOrProcessing ? (
                                  <MiniGridLoading size={36} gridDim={Math.round(Math.sqrt(s.gridSize || 49))} />
                                ) : (
                                  <MiniGrid gridData={gridData} size={36} />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">
                    {currentLocation?.name || "Select a location"}
                  </CardTitle>
                  {displayScan && (
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {displayScan.status === "completed"
                        ? `Scanned ${displayScan.scannedAt ? new Date(displayScan.scannedAt).toLocaleString() : ""}`
                        : `Status: ${displayScan.status}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {displayScan?.status === "completed" && (
                    <Badge variant="outline" className="text-emerald-500 border-emerald-500/30">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                    </Badge>
                  )}
                  {(displayScan?.status === "pending" || displayScan?.status === "processing") && (
                    <Badge variant="outline" className="text-brand-orange border-brand-orange/30 animate-pulse">
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processing
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isFailed && displayScan ? (
                <div className="flex flex-col items-center py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                    <X className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-lg font-semibold">Scan Failed</h3>
                  <p className="text-sm text-muted-foreground mt-2 max-w-sm">
                    {(displayScan as any).errorMessage
                      ? "The scan service returned an error. This is usually transient — try again."
                      : "The scan could not be completed. This may be due to a transient service hiccup or rate limit. Try again."}
                  </p>
                  {(displayScan as any).errorMessage && (
                    <p className="text-xs text-red-400 mt-2 font-mono max-w-md break-words" data-testid="text-scan-error">
                      {(displayScan as any).errorMessage}
                    </p>
                  )}
                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      onClick={() => retryScanMutation.mutate(displayScan.id)}
                      disabled={retryScanMutation.isPending}
                      data-testid="button-retry-failed"
                    >
                      {retryScanMutation.isPending ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}
                      Retry Scan
                    </Button>
                    <Button variant="outline" onClick={() => setActiveScanId(null)} data-testid="button-dismiss-failed">
                      Dismiss
                    </Button>
                  </div>
                </div>
              ) : displayScan && (
                  isProcessing ||
                  (displayScan.status === "completed" && theaterScanIds.has(displayScan.id) && !theaterDoneIds.has(displayScan.id))
                ) ? (
                <GeogridScanTheater
                  scan={displayScan}
                  points={displayPoints}
                  centerLat={displayScan.centerLat ? parseFloat(displayScan.centerLat) : (currentLocation?.lat || 0)}
                  centerLng={displayScan.centerLng ? parseFloat(displayScan.centerLng) : (currentLocation?.lng || 0)}
                  keyword={keywords.find(k => k.id === (displayScan.keywordId || selectedKeywordId))?.keyword}
                  onComplete={() => {
                    setTheaterDoneIds(prev => prev.has(displayScan.id) ? prev : new Set(prev).add(displayScan.id));
                  }}
                />
              ) : displayScan && displayScan.status === "completed" ? (
                <ScanResultsView
                  scan={displayScan}
                  points={displayPoints}
                  locationName={currentLocation?.name || ""}
                  centerLat={displayScan.centerLat ? parseFloat(displayScan.centerLat) : (currentLocation?.lat || 0)}
                  centerLng={displayScan.centerLng ? parseFloat(displayScan.centerLng) : (currentLocation?.lng || 0)}
                  keyword={keywords.find(k => k.id === (displayScan.keywordId || selectedKeywordId))?.keyword}
                  onRunScan={!locationNeedsGeocode && keywords.length > 0 ? () => {
                    runScanMutation.mutate({
                      gridSize: displayScan.gridSize,
                      radius: displayScan.radius,
                      keywordId: displayScan.keywordId,
                    });
                  } : undefined}
                  isScanning={runScanMutation.isPending || (isProcessing as boolean)}
                  scanLabel={`Run Scan (${displayScan.gridSize * displayScan.gridSize} pts)`}
                  onDeleteScan={() => deleteScanMutation.mutate(displayScan.id)}
                  onExport={() => {
                    const comps = buildCompetitorData(displayPoints, currentLocation?.name || "");
                    const headers = ["Rank", "Business", "Address", "AGR", "ATGR", "SoLV %", "Rating", "Reviews", "Score"];
                    const rows = comps.map((c, i) => [
                      i + 1,
                      `"${c.name.replace(/"/g, '""')}"`,
                      `"${(c.address || "").replace(/"/g, '""')}"`,
                      c.avgRank, c.atgr, c.solv, c.avgRating, c.totalReviews, c.score,
                    ]);
                    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `geogrid-report-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  keywordScans={scans.filter(s => s.status === "completed" && s.keywordId === displayScan.keywordId)}
                  activeScanId={activeScanId}
                  onSelectScan={(scanId) => setActiveScanId(scanId)}
                />
              ) : (
                <div className="flex flex-col items-center py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Grid3X3 className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">No Scan Data</h3>
                  <p className="text-sm text-muted-foreground mt-2 max-w-sm">
                    Add keywords and run your first GeoGrid scan to see how your business
                    ranks across your service area.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <OutOfCreditsDialog
        open={outOfCreditsOpen}
        onOpenChange={setOutOfCreditsOpen}
        details={outOfCreditsDetails}
      />
    </div>
  );
}
